/**
 * Composable per gestione CRUD di tab relazionali (Master-Relation pattern)
 * Unifica search/load + operazioni batch commit
 */

import { ref, readonly } from 'vue';
import { useNotification } from './useNotification';
import { useRouter } from './useNuxtRouter';

export interface TabRelationCrudOptions<T> {
	// Service functions (stesso service per tutto) - OBBLIGATORIE
	search: (masterId: number) => Promise<T[]>;
	add: (item: Partial<T>) => Promise<T>;
	update: (id: number, item: Partial<T>) => Promise<T>;
	delete: (id: number) => Promise<void>;

	// Data mapping
	masterId: number;
	mapForSave: (item: T) => Partial<T>;
	sortFn?: (a: T, b: T) => number; // Optional sort function for items

	// Dependencies injection
	withLoading?: (fn: () => Promise<void>, name: string) => Promise<void>;
	setAsPristine?: () => void;
	isDirty?: import('vue').Ref<boolean>; // Per controlli stato
	isLoading?: import('vue').Ref<boolean>; // Per controlli stato
}

export function useTabRelationCrud<T extends Record<string, any>>(
	options: TabRelationCrudOptions<T>,
) {
	const items = ref<T[]>([]);
	const { success, error } = useNotification();
	const router = useRouter();

	// SEARCH/LOAD
	async function loadItems(): Promise<void> {
		if (options.withLoading) {
			await options.withLoading(async () => {
				await performLoad();
			}, 'loadItems');
		} else {
			await performLoad();
		}
	}

	async function performLoad(): Promise<void> {
		try {
			const loadedItems = await options.search(options.masterId);
			// Apply optional sort function
			if (options.sortFn) {
				items.value = loadedItems.sort(options.sortFn) as any;
			} else {
				items.value = loadedItems as any;
			}
			if (options.setAsPristine) {
				options.setAsPristine();
			}
		} catch (err) {
			console.error('❌ ERROR useTabRelationCrud.loadItems:', err);
			error('Errore durante il caricamento! Ricaricare la pagina e riprovare.');
			router.back(); // Automatico!
		}
	}

	// BATCH COMMIT
	async function commitChanges(): Promise<boolean> {
		// Controlli stato PRIMA dell'elaborazione (pattern coerente con useFormUpsert)
		if (options.isLoading?.value || (options.isDirty && !options.isDirty.value)) {
			return true; // Exit early se sta caricando o non è dirty
		}

		try {
			const itemsToSave = items.value.filter((item: any) => !item.id).map(options.mapForSave);

			const itemsToUpdate = items.value
				.filter((item: any) => item.id && item.modified && !item.toBeDeleted)
				.map(options.mapForSave);

			const itemsToDelete = items.value
				.filter((item: any) => item.id && item.toBeDeleted)
				.map(options.mapForSave);

			const promises: Promise<any>[] = [
				...itemsToSave.map(options.add),
				...itemsToUpdate.map((item: any) => options.update(item.id, item)),
				...itemsToDelete.map((item: any) => options.delete(item.id)),
			];

			if (promises.length === 0) {
				return true; // Nessuna modifica da salvare
			}

			const results = await Promise.allSettled(promises);
			const hasErrors = results.some((x) => x.status === 'rejected');

			if (hasErrors) {
				error('Si sono verificate uno o più errori durante il salvataggio!');
				return false;
			} else {
				success('Modifiche salvate con successo.');
				if (options.setAsPristine) {
					options.setAsPristine();
				}
				await loadItems(); // Reload dopo save
				return true;
			}
		} catch (err) {
			console.error('❌ ERROR useTabRelationCrud.commitChanges:', err);
			error('Errore durante il salvataggio!');
			return false;
		}
	}

	return {
		items: readonly(items),
		loadItems,
		commitChanges,
	};
}
